import { AppMessageSchema, LaunchGrantMessageSchema } from "../../shared/schemas";
import { launchApp } from "../api/client";
import { activeFrames, type ActiveAppFrame } from "../app-frames";
import { DesktopFrameBroker } from "./broker";

interface FrameMessageControllerOptions {
  getFrame: () => HTMLIFrameElement | null;
  onCatalogActivity: () => void;
  onLaunchError: () => void;
  onSessionExpired: () => void;
}

export class FrameMessageController {
  readonly #broker = new DesktopFrameBroker();
  readonly #options: FrameMessageControllerOptions;
  #launchInFlight = false;
  #launchedWindow: WindowProxy | null = null;

  constructor(options: FrameMessageControllerOptions) {
    this.#options = options;
  }

  start(): void {
    window.addEventListener("message", this.#messageListener);
  }

  async closeFrame(source: WindowProxy): Promise<void> {
    await this.#broker.closeFrame(source);
    activeFrames.delete(source);
  }

  resetLaunch(): void {
    this.#launchInFlight = false;
    this.#launchedWindow = null;
  }

  async dispose(): Promise<void> {
    window.removeEventListener("message", this.#messageListener);
    const source = this.#options.getFrame()?.contentWindow;
    if (source) await this.closeFrame(source);
    await this.#broker.dispose();
  }

  readonly #messageListener = (event: MessageEvent<unknown>): void => {
    void this.#handleMessage(event);
  };

  async #handleMessage(event: MessageEvent<unknown>): Promise<void> {
    const iframe = this.#options.getFrame();
    const source = iframe?.contentWindow;
    if (!iframe || !source || event.source !== source) return;
    const active = activeFrames.get(source);
    if (event.origin !== active?.origin) return;
    if (await this.#broker.handleMessage(event)) return;
    const parsed = AppMessageSchema.safeParse(event.data);
    if (!parsed.success) return;
    if (parsed.data.type === "platform:app-session-expired") {
      await this.#expireSession(source);
      return;
    }
    if (parsed.data.appInstanceId !== active.appInstanceId) return;
    await this.#launch(source, active);
  }

  async #expireSession(source: WindowProxy): Promise<void> {
    await this.closeFrame(source);
    this.resetLaunch();
    this.#options.onSessionExpired();
  }

  async #launch(source: WindowProxy, active: ActiveAppFrame): Promise<void> {
    if (this.#launchInFlight || this.#launchedWindow === source || !navigator.onLine) return;
    this.#launchInFlight = true;
    try {
      const launch = await launchApp(active.appInstanceId, active.intendedPath);
      source.postMessage(
        LaunchGrantMessageSchema.parse({
          type: "platform:launch-grant",
          appInstanceId: active.appInstanceId,
          grant: launch.grant,
        }),
        active.origin,
      );
      this.#launchedWindow = source;
      this.#options.onCatalogActivity();
    } catch {
      this.#options.onLaunchError();
    } finally {
      this.#launchInFlight = false;
    }
  }
}
